import { useState, useEffect } from 'react';

/**
 * Hook to control homepage animations
 * 
 * Only animates on first visit. On refresh or return navigation,
 * content appears instantly without animations for a better UX.
 * Uses sessionStorage to track if animations have already run.
 * 
 * @returns {boolean} hasAnimated - true if animations should be skipped
 */
export const useHasAnimated = () => {
  const [hasAnimated, setHasAnimated] = useState(() => {
    // Check immediately on mount (synchronous check)
    if (typeof window !== 'undefined') {
      return sessionStorage.getItem('homepage_animations_seen') === 'true';
    }
    return false;
  });

  useEffect(() => {
    // If already seen, skip animations
    if (hasAnimated) {
      return;
    }

    // Mark as seen after animations complete
    // Max delay is 1000ms, add buffer for completion
    const timer = setTimeout(() => {
      if (typeof window !== 'undefined') {
        sessionStorage.setItem('homepage_animations_seen', 'true');
        setHasAnimated(true);
      }
    }, 1500); // Wait for all animations to complete (~1000ms max delay + buffer)
    
    return () => clearTimeout(timer);
  }, [hasAnimated]);

  return hasAnimated;
};
